import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomButton from "components/common/CustomButton";
import {
  IntegrationDetail,
  useTriggerIntegrationRunMutation,
} from "features/integrationsBeta/integrationsBeta";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";
import RunNowDialog from "./RunNowDialog";

type Props = {
  integration: IntegrationDetail;
  /** Called after a run was accepted (the caller jumps to page 1). */
  onTriggered: () => void;
};

/**
 * "Run now" for the internal history: a plain confirmation for schedule-mode
 * integrations, a JSON payload prompt for webhook-mode ones (their source step
 * consumes the event, so an empty run would be guaranteed to fail).
 */
export default function RunNowControl({ integration, onTriggered }: Props) {
  const dispatch = useAppDispatch();
  const [triggerRun, { isLoading: isTriggering }] =
    useTriggerIntegrationRunMutation();
  const [payloadOpen, setPayloadOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const trigger = async (event_payload?: Record<string, unknown>) => {
    try {
      await triggerRun({
        integrationId: integration.id,
        event_payload,
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Run triggered. It will appear in the history shortly.",
        }),
      );
      setPayloadOpen(false);
      onTriggered();
    } catch {
      dispatch(
        pushNotification({
          severity: "error",
          message: "Could not trigger the run.",
        }),
      );
    }
  };

  const handleClick = () => {
    if (integration.trigger_mode === "WEBHOOK") {
      setPayloadOpen(true);
      return;
    }
    setConfirmOpen(true);
  };

  return (
    <>
      <CustomButton
        fullWidth={false}
        onClick={handleClick}
        disabled={isTriggering}
        startIcon={<PlayArrowIcon />}
      >
        Run now
      </CustomButton>
      {payloadOpen && (
        <RunNowDialog
          open
          onClose={() => setPayloadOpen(false)}
          onSubmit={(payload) => trigger(payload)}
          loading={isTriggering}
        />
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Run integration now"
        message={`Trigger a run of "${integration.name}" now? It will execute the full pipeline immediately.`}
        confirmLabel="Run now"
        loading={isTriggering}
        onConfirm={async () => {
          await trigger();
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
